import React, { useCallback, useEffect, useMemo, useState } from "react";
import { adminApi } from "@/admin/AdminAuthContext";
import { Loader2, Flame, Gem, Phone, Mail, MessageCircle, MapPin, Filter } from "lucide-react";
import { toast } from "sonner";

const FILTERS = [
  { key: "all", label: "Tous" },
  { key: "urgent", label: "🔥 Urgent" },
  { key: "premium", label: "💰 Premium" },
];

function PriorityBadges({ tags }) {
  if (!tags || tags.length === 0) return null;
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {tags.includes("urgent") && (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gradient-to-r from-orange-500/25 to-red-500/25 border border-orange-500/40 text-[10px] uppercase tracking-[0.1em] font-bold text-orange-200" data-testid="badge-urgent">
          <Flame size={10} /> Urgent
        </span>
      )}
      {tags.includes("premium") && (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gradient-to-r from-amber-500/25 to-yellow-500/25 border border-amber-500/40 text-[10px] uppercase tracking-[0.1em] font-bold text-amber-200" data-testid="badge-premium">
          <Gem size={10} /> Premium
        </span>
      )}
    </div>
  );
}

function LeadRow({ lead }) {
  const dateStr = useMemo(() => {
    try {
      return new Date(lead.created_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });
    } catch {
      return lead.created_at;
    }
  }, [lead.created_at]);

  const priorityRing =
    lead.priority_tags?.length > 0
      ? "border-orange-500/30 bg-gradient-to-br from-orange-500/[0.05] to-transparent"
      : "border-white/10 bg-white/[0.02]";

  return (
    <div
      className={`rounded-2xl border ${priorityRing} p-5 hover:border-white/25 transition`}
      data-testid={`lead-row-${lead.id}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h3 className="font-medium text-white truncate">{lead.name}</h3>
            <PriorityBadges tags={lead.priority_tags} />
            {lead.priority_score > 0 && (
              <span className="text-[10px] font-mono text-orange-300/60">score {lead.priority_score}/40</span>
            )}
            {lead.ab_variant && (
              <span
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] uppercase tracking-[0.1em] font-bold ${
                  lead.ab_variant === "a"
                    ? "bg-sky-500/15 border-sky-500/40 text-sky-200"
                    : "bg-violet-500/15 border-violet-500/40 text-violet-200"
                }`}
                title={`A/B variant : ${lead.ab_variant.toUpperCase()}`}
                data-testid={`ab-variant-${lead.ab_variant}`}
              >
                Variant {lead.ab_variant.toUpperCase()}
              </span>
            )}
          </div>
          <div className="mt-1.5 flex items-center gap-4 text-xs text-white/55 flex-wrap">
            <span className="inline-flex items-center gap-1">
              <Mail size={11} />
              <a href={`mailto:${lead.email}`} className="hover:text-white/85 transition truncate max-w-[220px]">{lead.email}</a>
            </span>
            <span className="inline-flex items-center gap-1">
              <Phone size={11} />
              <a href={`tel:${lead.phone}`} className="hover:text-white/85 transition">{lead.phone}</a>
            </span>
            <a
              href={`https://wa.me/${lead.phone.replace(/\D/g, "")}`}
              target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1 text-emerald-300/85 hover:text-emerald-200 transition"
              data-testid={`lead-whatsapp-${lead.id}`}
            >
              <MessageCircle size={11} /> WhatsApp
            </a>
            <span className="inline-flex items-center gap-1 text-white/40">
              <MapPin size={11} /> Maurice
            </span>
          </div>
          <div className="mt-3 flex items-center gap-2 flex-wrap text-[11px]">
            <span className="px-2 py-1 rounded-md bg-white/5 border border-white/10 text-white/70">
              <span className="text-white/40 mr-1">Offre :</span>{lead.offer_label || lead.offer || "—"}
            </span>
            {lead.budget_label && (
              <span className="px-2 py-1 rounded-md bg-white/5 border border-white/10 text-white/70">
                <span className="text-white/40 mr-1">Budget :</span>{lead.budget_label}
              </span>
            )}
            {lead.timeline_label && (
              <span className="px-2 py-1 rounded-md bg-white/5 border border-white/10 text-white/70">
                <span className="text-white/40 mr-1">Délai :</span>{lead.timeline_label}
              </span>
            )}
            <span className="px-2 py-1 rounded-md bg-white/5 border border-white/10 text-white/60">
              <span className="text-white/40 mr-1">Site existant :</span>{lead.has_website === "yes" ? "Oui" : "Non"}
            </span>
          </div>
        </div>
        <div className="text-right text-[11px] text-white/40 shrink-0">
          <div data-testid={`lead-date-${lead.id}`}>{dateStr}</div>
          {(lead.utm_source || lead.utm_campaign) && (
            <div className="mt-1 text-white/35">
              {lead.utm_source}{lead.utm_campaign ? ` / ${lead.utm_campaign}` : ""}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AdminLeadsMaurice() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, urgent: 0, premium: 0, ab_variants: { a: 0, b: 0 }, ab_views: { a: 0, b: 0 } });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter === "all" ? {} : { filter_tag: filter };
      const { data } = await adminApi.get("/admin/leads/site-location-maurice", { params });
      setItems(data.items || []);
      setStats(data.stats || { total: 0, urgent: 0, premium: 0, ab_variants: { a: 0, b: 0 }, ab_views: { a: 0, b: 0 } });
    } catch (e) {
      toast.error("Impossible de charger les leads Maurice.");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="px-6 md:px-10 py-10 max-w-7xl" data-testid="admin-leads-maurice">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs tracking-[2px] text-white/40 font-semibold">— LEADS LANDING PAGE</div>
          <h1 className="mt-2 font-outfit text-3xl md:text-4xl font-light tracking-tight inline-flex items-center gap-3">
            🇲🇺 Maurice — Site Location
            <span className="text-base text-white/45 font-light">({stats.total})</span>
          </h1>
          <p className="text-white/55 text-sm mt-2">
            Leads capturés depuis <span className="text-white/85 font-mono text-xs">/site-location-maurice</span>, triés par priorité (🔥 Urgent + 💰 Premium en haut).
          </p>
          {(stats.ab_variants?.a > 0 || stats.ab_variants?.b > 0 || stats.ab_views?.a > 0 || stats.ab_views?.b > 0) && (
            <div className="mt-3 inline-flex items-center gap-3 px-3 py-2 rounded-xl bg-white/[0.02] border border-white/8 text-[11px]" data-testid="ab-stats">
              <span className="text-white/40 uppercase tracking-[0.14em] font-semibold text-[10px]">A/B CTA</span>
              <span className="inline-flex items-center gap-1.5">
                <span className="px-2 py-0.5 rounded-md bg-sky-500/15 border border-sky-500/40 text-sky-200 font-bold text-[10px]">A</span>
                <span className="text-white/70">
                  &quot;Demander mon devis gratuit&quot; · <span className="text-white font-semibold">{stats.ab_variants?.a || 0}</span> leads / <span className="text-white/60">{stats.ab_views?.a || 0} vues</span>
                </span>
              </span>
              <span className="text-white/20">|</span>
              <span className="inline-flex items-center gap-1.5">
                <span className="px-2 py-0.5 rounded-md bg-violet-500/15 border border-violet-500/40 text-violet-200 font-bold text-[10px]">B</span>
                <span className="text-white/70">
                  &quot;Recevoir mon devis sous 24h&quot; · <span className="text-white font-semibold">{stats.ab_variants?.b || 0}</span> leads / <span className="text-white/60">{stats.ab_views?.b || 0} vues</span>
                </span>
              </span>
              {(() => {
                const aLeads = stats.ab_variants?.a || 0;
                const bLeads = stats.ab_variants?.b || 0;
                const aViews = stats.ab_views?.a || 0;
                const bViews = stats.ab_views?.b || 0;
                // Only compute a winner when both variants have meaningful exposure
                const MIN_VIEWS_PER_VARIANT = 50;
                if (aViews < MIN_VIEWS_PER_VARIANT || bViews < MIN_VIEWS_PER_VARIANT) {
                  return (
                    <span className="text-white/35 italic text-[10px]" data-testid="ab-insufficient">
                      (≥ {MIN_VIEWS_PER_VARIANT} vues/variant requis)
                    </span>
                  );
                }
                const rateA = aLeads / aViews;
                const rateB = bLeads / bViews;
                if (rateA === rateB) return null;
                const winner = rateA > rateB ? "A" : "B";
                const winnerRate = Math.max(rateA, rateB);
                const loserRate = Math.min(rateA, rateB);
                const upliftPct = loserRate > 0 ? (((winnerRate - loserRate) / loserRate) * 100).toFixed(0) : "—";
                return (
                  <span className="ml-1 px-2 py-0.5 rounded-md bg-emerald-500/15 border border-emerald-500/35 text-emerald-200 font-semibold text-[10px]" data-testid="ab-winner">
                    🏆 {winner} · {(winnerRate * 100).toFixed(1)}% (+{upliftPct}%)
                  </span>
                );
              })()}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-orange-500/10 border border-orange-500/25 text-[11px] text-orange-200" data-testid="stats-urgent">
            <Flame size={11} /> {stats.urgent} Urgent
          </span>
          <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/25 text-[11px] text-amber-200" data-testid="stats-premium">
            <Gem size={11} /> {stats.premium} Premium
          </span>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-6 flex-wrap">
        <Filter size={13} className="text-white/40" />
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setFilter(f.key)}
            data-testid={`filter-${f.key}`}
            className={`px-3.5 py-1.5 rounded-full text-xs font-medium transition border ${
              filter === f.key
                ? "bg-white/10 border-white/25 text-white"
                : "bg-white/[0.02] border-white/10 text-white/55 hover:text-white hover:bg-white/[0.05]"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-10 flex items-center justify-center gap-2 text-white/40 text-sm">
          <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-12 text-center" data-testid="leads-empty">
          <div className="text-sm text-white/65">Aucun lead pour l&apos;instant.</div>
          <div className="mt-1 text-xs text-white/40">
            Les nouveaux leads capturés sur la landing page Maurice apparaîtront ici en priorité.
          </div>
        </div>
      ) : (
        <div className="space-y-3" data-testid="leads-list">
          {items.map((lead) => (
            <LeadRow key={lead.id} lead={lead} />
          ))}
        </div>
      )}
    </div>
  );
}
